import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { color, borderColor } from 'styled-system';

import { themeGet } from '@styled-system/theme-get';

import Account from './components/Account';
import MobileMenu from './components/MobileMenu';
import IntercomButton from './components/IntercomButton';
import TileSearch from './components/TileSearch';
import Tooltip from '../../shared/Tooltip';
import BoardSelect from './components/BoardSelect';
import GuestButtons from './components/GuestButtons';
import useDropdown from '../../lib/hooks/useDropdown';
import LanguageWrapper from './components/LanguageWrapper';
import ToggleNavBar from './components/ToggleNavBar';
import { isAdmin } from '../../lib/utils/roles';

// The translate button is show only on the following paths
// host.com/tiles , host.com/activity , host.com/ard/public-slug/tiles and host.com/ard/public-slug/activity
const showLanguageWrapper = () => {
  const ALLOWED_PATHS_REGEX = /(\/ard\/.+\/(tiles|activity))|(^\/(tiles|activity))/;
  const path = window.location.pathname;
  return ALLOWED_PATHS_REGEX.test(path);
};

export default function Header({
  logo,
  intercomData,
  shouldConvertGuest,
  guestUserResetPath,
  home,
}) {
  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();

  const { userType, browserLanguage, isRequestedLanguageDefault } = useSelector(
    state => state.currentUser,
  );

  return (
    <Wrapper>
      {isAdmin(userType) && <ToggleNavBar />}
      <LogoLink href={home}>
        {logo === 'false' ? (
          <LogoPlaceholder
            id="logoPlaceholder"
            color="black.40"
            borderColor="black.10"
            bg="black.5"
          >
            Logo
          </LogoPlaceholder>
        ) : (
          <Logo as="img" src={logo} />
        )}
      </LogoLink>
      {userType !== 'guest' ? (
        <>
          <BoardSelect userType={userType} />
          <TileSearch />
          <IntercomButton intercomData={intercomData} />
          <Account
            userType={userType}
            setShowDropdown={setShowDropdown}
            showDropdown={showDropdown}
            buttonRef={buttonRef}
            dropdownRef={dropdownRef}
          />
          {!showDropdown && (
            <>
              <Tooltip id="SearchButton" />
              <Tooltip id="Account" />
              <Tooltip id="IntercomButton" />
            </>
          )}
        </>
      ) : (
        <GuestButtons
          guestUserResetPath={guestUserResetPath}
          shouldConvertGuest={shouldConvertGuest}
        />
      )}
      {showLanguageWrapper() && (
        <LanguageWrapper
          browserLanguage={browserLanguage}
          isRequestedLanguageDefault={isRequestedLanguageDefault}
        />
      )}
      <MobileMenu userType={userType} guestUserResetPath={guestUserResetPath} />
    </Wrapper>
  );
}

Header.propTypes = {
  logo: PropTypes.string,
  intercomData: PropTypes.object,
  shouldConvertGuest: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  guestUserResetPath: PropTypes.string,
  home: PropTypes.string,
};

const Wrapper = styled.header`
  height: 40px;
  display: flex;
  padding: 8px 16px;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  justify-content: space-between;
  align-items: center;
  z-index: 2;

  ${themeGet('mediaQueries.forMediumUp')} {
    height: 70px;
    padding: 0 40px 0 0;
    background-color: ${themeGet('colors.white')};
    border-bottom: 1px solid ${themeGet('colors.black.10')};
    box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.04);
  }
`;

const LogoLink = styled.a`
  padding-left: 24px;
`;

const Logo = styled.div`
  border-radius: 8px;
  max-width: 86px;
  max-height: 24px;

  ${themeGet('mediaQueries.forMediumUp')} {
    max-width: 150px;
    max-height: 55px;
  }
`;

const LogoPlaceholder = styled(Logo)`
  ${color};
  ${borderColor};
  border: 1px solid;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: 18px;
  width: 86px;
  height: 24px;

  ${themeGet('mediaQueries.forMediumUp')} {
    width: 150px;
    height: 55px;
  }
`;
